import { useAuth } from '@clerk/clerk-react';

import { trpc } from '@/lib/api/trpc';

/**
 * Hook to check the payment method status for the current account.
 * Used by the PaymentGuard to determine if a user needs to set up payment.
 *
 * Resolved on the provider that bills the account (Stripe for legacy
 * accounts, Checkout.com for new ones):
 * - the provider-side customer exists
 * - it has a default payment method (required for automatic billing)
 * - the method is not expired (for cards)
 * `pendingProvider` is set while an account manager is moving the account
 * to another provider and the merchant still has to add a card there.
 */
export function usePaymentStatus() {
  const { isLoaded, isSignedIn } = useAuth();

  const { data, isLoading, isError, refetch } = trpc.billing.getPaymentMethodStatus.useQuery(
    {},
    {
      enabled: isLoaded && isSignedIn,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  return {
    hasPaymentMethod: data?.hasPaymentMethod ?? false,
    isValid: data?.isValid ?? false,
    billingEmail: data?.billingEmail,
    provider: data?.provider ?? null,
    pendingProvider: data?.pendingProvider ?? null,
    customerRef: data?.customerRef ?? null,
    isLoading: !isLoaded || !isSignedIn || isLoading,
    isError,
    hasCachedData: !!data,
    refetch,
  };
}

